import { InputHTMLAttributes, forwardRef } from 'react';
import { cn } from '../../lib/utils';

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
    label?: string;
    error?: string;
}

/**
 * Checkbox — Premium checkbox with teal checked state and consistent sizing.
 * Compatible with React Hook Form via `register()`.
 *
 * @example
 * <Checkbox label="Shuffle Questions" {...register('shuffleQuestions')} />
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
    ({ label, error, className, id, ...rest }, ref) => {
        const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');
        return (
            <div className="flex flex-col gap-1">
                <label htmlFor={inputId} className="flex items-center gap-2.5 cursor-pointer group">
                    <input
                        ref={ref}
                        id={inputId}
                        type="checkbox"
                        className={cn(
                            'w-4 h-4 rounded-md border-2 border-slate-300',
                            'text-teal-primary accent-teal-primary',
                            'focus-visible:ring-4 focus-visible:ring-teal-primary/20 focus-visible:border-teal-primary',
                            'disabled:opacity-50 disabled:cursor-not-allowed',
                            'transition-colors',
                            className
                        )}
                        {...rest}
                    />
                    {label && (
                        <span className="text-sm text-slate-700 font-medium group-hover:text-slate-900 transition-colors select-none">
                            {label}
                        </span>
                    )}
                </label>
                {error && <p className="text-xs font-medium text-red-500 ml-6">{error}</p>}
            </div>
        );
    }
);
Checkbox.displayName = 'Checkbox';

// ---------------------------------------------------------------------------
// Radio
// ---------------------------------------------------------------------------
interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
    label?: string;
    /** Secondary line under the label; part of the same clickable label. */
    description?: string;
    /** Classes for the wrapping <label> (e.g. to render a selectable card). */
    wrapperClassName?: string;
}

export const Radio = forwardRef<HTMLInputElement, RadioProps>(
    ({ label, description, wrapperClassName, className, id, ...rest }, ref) => {
        const inputId = id || `radio-${rest.value}`;
        return (
            <label
                htmlFor={inputId}
                className={cn(
                    'flex gap-2.5 cursor-pointer group',
                    description ? 'items-start' : 'items-center',
                    wrapperClassName
                )}
            >
                <input
                    ref={ref}
                    id={inputId}
                    type="radio"
                    className={cn(
                        'w-4 h-4 border-2 border-slate-300',
                        description && 'mt-0.5',
                        'text-teal-primary accent-teal-primary',
                        'focus-visible:ring-4 focus-visible:ring-teal-primary/20 focus-visible:border-teal-primary',
                        'disabled:opacity-50 disabled:cursor-not-allowed transition-colors',
                        className
                    )}
                    {...rest}
                />
                {(label || description) && (
                    <span className="flex flex-col">
                        {label && (
                            <span className="text-sm text-slate-700 font-medium group-hover:text-slate-900 transition-colors select-none">
                                {label}
                            </span>
                        )}
                        {description && (
                            <span className="text-xs text-slate-500 leading-relaxed select-none">
                                {description}
                            </span>
                        )}
                    </span>
                )}
            </label>
        );
    }
);
Radio.displayName = 'Radio';
